import { useQuery } from '@tanstack/react-query';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import Api from '../../../../api/Api';
import { CreateReportData } from '../../../../api/endpoints/Service';
import { QUERY_KEYS } from '../../../../queries/queryKeys';
import useCreateReportMutation from '../../../../queries/useCreateReportMutation';
import useAuthStore from '../../../../store/authStore';
import useDatasetStore from '../../../../store/datasetStore';
import { useReportInputStore } from '../../../../store/reportInputStore';
import { IReports } from '../../../../types/reports';
import {
  SubscriptionType,
  WaterdataGroup,
} from '../../../../types/userGroupPermissions';
import { useSubscriptionStatus } from '../../../../utils/hooks';
import { isOfflineDatasetSelectionGuard } from '../../../../utils/offlineDatasetGuard';
import { userGroupValidation } from '../../../../utils/userGroupValidation';

type MetadataWithTimeDefinition = {
  TimeDefinition?: string;
} | null;

export const REQUEST_REPORT_STATUS = {
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  PENDING: 'PENDING',
} as const;

export const useOndemandParams = (params: IReports) => {
  const {
    datasets,
    defaultDataset,
    pointName,
    location,
    setDatasetIsOfflineWarningVisible,
  } = params;

  const datasetSW = useReportInputStore((state) => state.datasetSW);
  const setDatasetSW = useReportInputStore((state) => state.setDatasetSW);

  const datasetHD = useReportInputStore((state) => state.datasetHD);
  const setDatasetHD = useReportInputStore((state) => state.setDatasetHD);

  const datasetWind = useReportInputStore((state) => state.datasetWind);
  const setDatasetWind = useReportInputStore((state) => state.setDatasetWind);

  const timeStep = useReportInputStore((state) => state.timeStep);
  // const setTimeStep = useReportInputStore((state) => state.setTimeStep);

  const startDate = useReportInputStore((state) => state.startDate);
  const setStartDate = useReportInputStore((state) => state.setStartDate);

  const endDate = useReportInputStore((state) => state.endDate);
  const setEndDate = useReportInputStore((state) => state.setEndDate);

  const user = useAuthStore((state) => state.user);
  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);
  const { isActiveSubscriber } = useSubscriptionStatus();

  const setRequestedReportsCount = useReportInputStore(
    (state) => state.setRequestedReportsCount
  );

  const requestedReportsCount = useReportInputStore(
    (state) => state.requestedReportsCount
  );

  const metadataQuerySW = useQuery({
    queryKey: [QUERY_KEYS.dataset.metadata, datasetSW],
    queryFn: async () =>
      datasetSW ? await Api.metadata.getMetadata(datasetSW) : null,
    enabled: !!datasetSW,
  });

  const metadataQueryHD = useQuery({
    queryKey: [QUERY_KEYS.dataset.metadata, datasetHD],
    queryFn: async () =>
      datasetHD ? await Api.metadata.getMetadata(datasetHD) : null,
    enabled: !!datasetHD,
  });

  const metadataQueryWind = useQuery({
    queryKey: [QUERY_KEYS.dataset.metadata, datasetWind],
    queryFn: async () =>
      datasetWind ? await Api.metadata.getMetadata(datasetWind) : null,
    enabled: !!datasetWind,
  });

  const { mutate, isPending, isError, isSuccess } = useCreateReportMutation({
    onSuccess: () => {
      setRequestedReportsCount();
    },
  });

  const getValidatedRange = useCallback(
    (metadata: MetadataWithTimeDefinition) => {
      if (!metadata?.TimeDefinition || !user) return null;
      return userGroupValidation(
        user?.waterdataGroup
          ? WaterdataGroup[user.waterdataGroup as keyof typeof WaterdataGroup]
          : null,
        user?.subscriptionType
          ? SubscriptionType[
              user.subscriptionType as keyof typeof SubscriptionType
            ]
          : null,
        metadata.TimeDefinition,
        user?.confidentialDatasetIds || []
      ).dateRange;
    },
    [user]
  );

  const validatedDateRangeSW = useMemo(
    () => getValidatedRange(metadataQuerySW.data),
    [metadataQuerySW.data, getValidatedRange]
  );
  const validatedDateRangeHD = useMemo(
    () => getValidatedRange(metadataQueryHD.data),
    [metadataQueryHD.data, getValidatedRange]
  );
  const validatedDateRangeWind = useMemo(
    () => getValidatedRange(metadataQueryWind.data),
    [metadataQueryWind.data, getValidatedRange]
  );

  const isSWDatasetOffline = metadataQuerySW.data?.IsOffline;
  const isHDDatasetOffline = metadataQueryHD.data?.IsOffline;
  const isWindDatasetOffline = metadataQueryWind.data?.IsOffline;

  const isSomeSelectedDatasetOffline =
    isSWDatasetOffline || isHDDatasetOffline || isWindDatasetOffline;

  const dateRangeFromMetadata = useMemo(() => {
    const validatedRanges = [
      validatedDateRangeSW,
      validatedDateRangeHD,
      validatedDateRangeWind,
    ].filter(Boolean) as [Moment, Moment][];
    if (validatedRanges.length === 0) return null;

    const calculatedStartDate = moment.max(validatedRanges.map((r) => r[0]));
    const calculatedEndDate = moment.min(validatedRanges.map((r) => r[1]));

    if (calculatedStartDate.isAfter(calculatedEndDate)) {
      console.warn('No date overlap between datasets after validation');
      return null;
    }
    return [calculatedStartDate, calculatedEndDate] as [Moment, Moment];
  }, [validatedDateRangeSW, validatedDateRangeHD, validatedDateRangeWind]);

  const getNonSubscriberStartDate = useCallback(
    (targetEndDate: Moment) => {
      const oneYearBeforeEndDate = targetEndDate.clone().subtract(1, 'year');

      if (!dateRangeFromMetadata) return oneYearBeforeEndDate;

      return moment.max(dateRangeFromMetadata[0], oneYearBeforeEndDate);
    },
    [dateRangeFromMetadata]
  );

  const doStartDateChange = useCallback(
    (value: Moment | null) => value && setStartDate(value),
    [setStartDate]
  );
  const doEndDateChange = useCallback(
    (value: Moment | null) => value && setEndDate(value),
    [setEndDate]
  );

  const disabledStartDate = useCallback(
    (current: Moment) => {
      if (!dateRangeFromMetadata || !endDate) return false;

      if (!isActiveSubscriber) {
        const allowedStartDate = getNonSubscriberStartDate(endDate);
        return !current.isSame(allowedStartDate, 'day');
      }

      return (
        (current && current > endDate) ||
        current.isBefore(dateRangeFromMetadata[0], 'day')
      );
    },
    [
      endDate,
      dateRangeFromMetadata,
      getNonSubscriberStartDate,
      isActiveSubscriber,
    ]
  );

  const disabledEndDate = useCallback(
    (current: Moment) => {
      if (!dateRangeFromMetadata || !startDate) return false;
      return (
        (current && current < startDate) ||
        current.isAfter(dateRangeFromMetadata[1], 'day')
      );
    },
    [startDate, dateRangeFromMetadata]
  );

  const handleDatasetChange = useCallback(
    (type: 'SW' | 'HD' | 'Wind') => (value: string) => {
      const actualValue = value === '-' ? '' : value;

      if (
        actualValue &&
        isOfflineDatasetSelectionGuard({
          datasetId: actualValue,
          datasetBriefs,
          setDatasetIsOfflineWarningVisible,
        })
      ) {
        return;
      }

      if (type === 'SW') setDatasetSW(actualValue);
      if (type === 'HD') setDatasetHD(actualValue);
      if (type === 'Wind') setDatasetWind(actualValue);
    },
    [
      datasetBriefs,
      setDatasetHD,
      setDatasetIsOfflineWarningVisible,
      setDatasetSW,
      setDatasetWind,
    ]
  );

  const getDefaultValue = useCallback(
    (key: string) => {
      return defaultDataset?.find((item) => item.key === key)?.value || '';
    },
    [defaultDataset]
  );

  const getLocationString = (location: number[]) => {
    const [lat, lng] = location;
    return `${lat}, ${lng}`;
  };

  const effectiveEndDate = useMemo(() => {
    if (!dateRangeFromMetadata) return endDate;
    const [validMin, validMax] = dateRangeFromMetadata;
    if (!endDate || endDate.isAfter(validMax) || endDate.isBefore(validMin))
      return validMax;
    return endDate;
  }, [endDate, dateRangeFromMetadata]);

  const effectiveStartDate = useMemo(() => {
    if (!isActiveSubscriber && effectiveEndDate) {
      return getNonSubscriberStartDate(effectiveEndDate);
    }

    if (!dateRangeFromMetadata) return startDate;
    const [validMin, validMax] = dateRangeFromMetadata;
    if (
      !startDate ||
      startDate.isBefore(validMin) ||
      startDate.isAfter(validMax)
    )
      return validMin;
    return startDate;
  }, [
    startDate,
    dateRangeFromMetadata,
    effectiveEndDate,
    getNonSubscriberStartDate,
    isActiveSubscriber,
  ]);

  useEffect(() => {
    if (isActiveSubscriber || !effectiveEndDate) return;

    const expectedStartDate = getNonSubscriberStartDate(effectiveEndDate);

    if (!startDate || !startDate.isSame(expectedStartDate, 'day')) {
      setStartDate(expectedStartDate);
    }
  }, [
    effectiveEndDate,
    getNonSubscriberStartDate,
    isActiveSubscriber,
    setStartDate,
    startDate,
  ]);

  const isDatasetSelected = !!datasetSW || !!datasetHD || !!datasetWind;

  const isPayloadForReportCallValid =
    !!effectiveStartDate &&
    !!effectiveEndDate &&
    !isSomeSelectedDatasetOffline &&
    isDatasetSelected;

  const handleRequestReport = useCallback(() => {
    if (!isPayloadForReportCallValid) {
      return;
    }
    const datasetIds = [datasetSW, datasetHD, datasetWind].filter(
      (id): id is string => id !== null
    );

    const payload: CreateReportData = {
      pointName,
      datasetIds,
      timestepInterval: Number(timeStep),
      startTime: moment(effectiveStartDate).format('YYYY-MM-DD'),
      endTime: moment(effectiveEndDate).format('YYYY-MM-DD'),
      location: getLocationString(location),
    };

    mutate(payload);
  }, [
    datasetSW,
    datasetHD,
    datasetWind,
    timeStep,
    effectiveStartDate,
    effectiveEndDate,
    location,
    pointName,
    isPayloadForReportCallValid,
    mutate,
  ]);

  const defineIsInitiallySelectedDataset = useCallback(
    ({
      value,
      datasetType,
      defaultDatasetType,
    }: {
      datasetType: 'Waves' | 'Ocean' | 'Atmosphere';
      defaultDatasetType: 'SW' | 'HD' | 'Wind';
      value: string | null;
    }) => {
      if (value === null) return getDefaultValue(defaultDatasetType);
      if (value === '') return '';
      const isCorrectDataset = datasets
        .filter(({ label }) => label === datasetType)
        .map(({ id }) => id)
        .includes(value);
      return isCorrectDataset ? value : getDefaultValue(defaultDatasetType);
    },
    [datasets, getDefaultValue]
  );

  useEffect(() => {
    setDatasetSW(
      defineIsInitiallySelectedDataset({
        value: datasetSW,
        datasetType: 'Waves',
        defaultDatasetType: 'SW',
      })
    );

    setDatasetHD(
      defineIsInitiallySelectedDataset({
        value: datasetHD,
        datasetType: 'Ocean',
        defaultDatasetType: 'HD',
      })
    );

    setDatasetWind(
      defineIsInitiallySelectedDataset({
        value: datasetWind,
        datasetType: 'Atmosphere',
        defaultDatasetType: 'Wind',
      })
    );
  }, [
    datasetHD,
    datasetSW,
    datasetWind,
    defineIsInitiallySelectedDataset,
    setDatasetHD,
    setDatasetSW,
    setDatasetWind,
  ]);

  const getRequestReportStatus = () => {
    if (isError) {
      return REQUEST_REPORT_STATUS.ERROR;
    } else if (isPending) {
      return REQUEST_REPORT_STATUS.PENDING;
    } else if (isSuccess) {
      return REQUEST_REPORT_STATUS.SUCCESS;
    }
  };

  return {
    datasetSW,
    datasetHD,
    datasetWind,
    timeStep,
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
    datasets,
    requestReportStatus: getRequestReportStatus(),

    doStartDateChange,
    doEndDateChange,
    disabledStartDate,
    disabledEndDate,
    handleDatasetChange,
    handleRequestReport,

    isSomeSelectedDatasetOffline,
    isSWDatasetOffline,
    isHDDatasetOffline,
    isWindDatasetOffline,
    isPayloadForReportCallValid,
    requestedReportsCount,
  };
};
